import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import Navbar from '../components/layout/Navbar';
import FilterPanel from '../components/common/FilterPanel';
import apiService from '../services/apiService';
import { useToast } from '../components/contexts/ToastContext';
import useDebounce from '../hooks/useDebounce';
import { downloadJSONAsXLSX } from '../utils/downloadHelpers';
import { getDateKey } from '../utils/dateHelpers';
import '../styles/pages/analytics.css';

const COLORS = ['#2ecc71', '#e74c3c', '#3498db', '#f39c12', '#9b59b6', '#1abc9c', '#e67e22', '#34495e'];

function Analytics() {
  const { showToast } = useToast();
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    fecha_inicio: '',
    fecha_termino: '',
    equipo_id: '',
    tipo_mantenimiento_id: '',
    categoria_equipo_id: ''
  });
  const [equipos, setEquipos] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [categorias, setCategorias] = useState([]);

  const debouncedFilters = useDebounce(filters, 500);

  // Cargar catálogos para filtros
  useEffect(() => {
    const fetchCatalogos = async () => {
      try {
        const [equiposRes, tiposRes, categoriasRes] = await Promise.all([
          apiService.getEquipos({ limit: 1000 }),
          apiService.getTiposOrdenTrabajo(),
          apiService.getCategoriasLista()
        ]);
        if (equiposRes.data) setEquipos(equiposRes.data);
        if (tiposRes.success) setTipos(tiposRes.data);
        if (categoriasRes.success) setCategorias(categoriasRes.data || []);
      } catch (error) {
        console.error('Error fetching catalogos:', error);
      }
    };
    fetchCatalogos();
  }, []);

  // Fetch KPIs
  const fetchKpis = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (debouncedFilters.fecha_inicio) params.fecha_inicio = debouncedFilters.fecha_inicio;
      if (debouncedFilters.fecha_termino) params.fecha_termino = debouncedFilters.fecha_termino;
      if (debouncedFilters.equipo_id) params.equipo_id = debouncedFilters.equipo_id;
      if (debouncedFilters.tipo_mantenimiento_id) params.tipo_mantenimiento_id = debouncedFilters.tipo_mantenimiento_id;
      if (debouncedFilters.categoria_equipo_id) params.categoria_equipo_id = debouncedFilters.categoria_equipo_id;

      const response = await apiService.getAnalyticsKpis(params);
      if (response.success) {
        setKpis(response.data);
      }
    } catch (error) {
      showToast('Error al cargar analytics', 'error');
    } finally {
      setLoading(false);
    }
  }, [debouncedFilters, showToast]);

  useEffect(() => {
    fetchKpis();
  }, [fetchKpis]);

  const handleFilterChange = (name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleExport = () => {
    if (!kpis) {
      showToast('No hay datos para exportar', 'warning');
      return;
    }
    try {
      const rows = [];
      // MTTR
      kpis.mttr.forEach(item => {
        rows.push({
          KPI: 'MTTR',
          Equipo: item.equipo_codigo,
          'Horas Promedio': item.mttr_horas ? Number(item.mttr_horas).toFixed(1) : '0',
          'OTs Completadas': item.total_completadas
        });
      });
      // Completitud
      kpis.completitud.forEach(item => {
        rows.push({
          KPI: 'Completitud',
          Mes: item.mes,
          Total: item.total,
          Completadas: item.completadas,
          'Porcentaje (%)': item.porcentaje
        });
      });
      // Cumplimiento
      rows.push({
        KPI: 'Cumplimiento Preventivo',
        'Total Preventivas': kpis.cumplimiento.total_preventivas,
        'A Tiempo': kpis.cumplimiento.a_tiempo,
        'Atrasadas': kpis.cumplimiento.atrasadas
      });

      const timestamp = getDateKey(new Date());
      downloadJSONAsXLSX(rows, 'Analytics', `analytics_${timestamp}`, [
        { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 15 }, { wch: 15 }
      ]);
      showToast('Datos exportados a Excel', 'success');
    } catch {
      showToast('Error al exportar', 'error');
    }
  };

  const filterConfig = [
    { name: 'fecha_inicio', type: 'date', label: 'Desde' },
    { name: 'fecha_termino', type: 'date', label: 'Hasta' },
    {
      name: 'equipo_id',
      type: 'select',
      placeholder: 'Todos los equipos',
      options: equipos.map(e => ({ value: e.id, label: e.nombre || e.codigo }))
    },
    {
      name: 'tipo_mantenimiento_id',
      type: 'select',
      placeholder: 'Todos los tipos',
      options: tipos.map(t => ({ value: t.id, label: t.nombre }))
    },
    {
      name: 'categoria_equipo_id',
      type: 'select',
      placeholder: 'Todas las categorías',
      options: categorias.map(c => ({ value: c.id, label: c.nombre }))
    }
  ];

  const actionConfig = [
    { label: 'Exportar Excel', icon: 'fa-file-excel', variant: 'secondary', onClick: handleExport }
  ];

  // Datos para gráfico de cumplimiento
  const cumplimientoData = kpis ? [
    { name: 'A tiempo', value: Number(kpis.cumplimiento.a_tiempo) || 0 },
    { name: 'Atrasadas', value: Number(kpis.cumplimiento.atrasadas) || 0 }
  ] : [];

  // Agrupar pendientes por categoría para stacked bar
  const pendientesAgrupadas = {};
  if (kpis?.pendientes) {
    kpis.pendientes.forEach(item => {
      const cat = item.categoria_nombre;
      if (!pendientesAgrupadas[cat]) pendientesAgrupadas[cat] = 0;
      pendientesAgrupadas[cat] += Number(item.pendientes_count);
    });
  }
  const pendientesData = Object.entries(pendientesAgrupadas).map(([nombre, count]) => ({
    nombre,
    pendientes: count
  }));

  return (
    <>
      <Navbar />
      <div className="container page-container">
        <h1 className="page-title">
          <i className="fas fa-chart-line"></i> Analytics
        </h1>

        <FilterPanel
          filters={filters}
          onFilterChange={handleFilterChange}
          filterConfig={filterConfig}
          actionConfig={actionConfig}
        />

        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Cargando KPIs...</p>
          </div>
        )}

        {!loading && kpis && (
          <div className="stats-grid analytics-grid">
            {/* MTTR por equipo */}
            <div className="card card-static">
              <div className="card-header">
                <h3 className="card-title">MTTR por Equipo (horas)</h3>
              </div>
              {kpis.mttr.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={kpis.mttr}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="equipo_codigo" tick={{ fontSize: 12 }} interval={0} angle={-30} textAnchor="end" height={80} />
                    <YAxis />
                    <Tooltip formatter={(value) => [`${Number(value).toFixed(1)} hrs`, 'MTTR']} />
                    <Bar dataKey="mttr_horas" fill="#3498db" name="MTTR (hrs)" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-secondary">Sin datos de MTTR para el periodo seleccionado</p>
              )}
            </div>

            {/* Tasa de completitud por mes */}
            <div className="card card-static">
              <div className="card-header">
                <h3 className="card-title">Tasa de Completitud (%)</h3>
              </div>
              {kpis.completitud.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={kpis.completitud}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                    <YAxis domain={[0, 100]} />
                    <Tooltip formatter={(value) => [`${value}%`, 'Completitud']} />
                    <Legend />
                    <Line type="monotone" dataKey="porcentaje" stroke="#2ecc71" strokeWidth={2} name="% Completadas" dot={{ r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-secondary">Sin datos de completitud para el periodo seleccionado</p>
              )}
            </div>

            {/* Cumplimiento preventivo */}
            <div className="card card-static">
              <div className="card-header">
                <h3 className="card-title">Cumplimiento Preventivo</h3>
              </div>
              {(cumplimientoData[0]?.value > 0 || cumplimientoData[1]?.value > 0) ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={cumplimientoData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      outerRadius={100}
                      dataKey="value"
                    >
                      {cumplimientoData.map((entry, index) => (
                        <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-secondary">Sin datos de cumplimiento preventivo para el periodo seleccionado</p>
              )}
            </div>

            {/* Actividades pendientes por categoría */}
            <div className="card card-static">
              <div className="card-header">
                <h3 className="card-title">Actividades Pendientes por Categoria</h3>
              </div>
              {pendientesData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={pendientesData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="nombre" tick={{ fontSize: 12 }} interval={0} angle={-30} textAnchor="end" height={80} />
                    <YAxis allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="pendientes" fill="#e74c3c" name="Pendientes" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-secondary">Sin actividades pendientes para el periodo seleccionado</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Analytics;
